<script setup lang="ts">
import { useSearch } from '@/composable/useSearch'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import Button from '@/components/ui/button/Button.vue'
import { Search, History, Loader2 } from 'lucide-vue-next'
import { ref, onMounted, onUnmounted, watch, nextTick, onBeforeUpdate } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const { searchQuery, searchResults, saveSearch, isLoading } = useSearch()
const open = ref(false)

// --- Keyboard Navigation State ---
const selectedIndex = ref(0)
const itemRefs = ref<HTMLElement[]>([])

// Reset item refs before Vue updates the DOM
onBeforeUpdate(() => {
  itemRefs.value = []
})

// Reset selection to the top whenever results change
watch(searchResults, () => {
  selectedIndex.value = 0
})

const goToKanji = async (kanji: string) => {
  await saveSearch(kanji)
  open.value = false
  router.push(`/kanjis/${kanji}`)
  setTimeout(() => {
    searchQuery.value = ''
  }, 300)
}

// Global shortcut to open the palette
const handleGlobalKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault()
    open.value = !open.value
  }
}

// Input-specific keyboard navigation
const handleInputKeyDown = async (e: KeyboardEvent) => {
  if (searchResults.value.length === 0) return

  if (e.key === 'ArrowDown') {
    e.preventDefault() // Stop cursor from jumping
    selectedIndex.value = (selectedIndex.value + 1) % searchResults.value.length
    scrollToItem(selectedIndex.value)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    selectedIndex.value =
      (selectedIndex.value - 1 + searchResults.value.length) % searchResults.value.length
    scrollToItem(selectedIndex.value)
  } else if (e.key === 'Enter') {
    e.preventDefault()
    goToKanji(searchResults.value[selectedIndex.value])
  }
}

// Auto-scroll the container so the highlighted item is always visible
const scrollToItem = async (index: number) => {
  await nextTick()
  const el = itemRefs.value[index]
  if (el) {
    el.scrollIntoView({ block: 'nearest' })
  }
}

onMounted(() => window.addEventListener('keydown', handleGlobalKeyDown))
onUnmounted(() => window.removeEventListener('keydown', handleGlobalKeyDown))
</script>

<template>
  <div>
    <Button variant="outline" size="icon" @click="open = true">
      <Search class="w-5 h-5" />
    </Button>

    <Dialog v-model:open="open">
      <DialogContent class="p-0 gap-0 overflow-hidden max-w-xl shadow-lg border-border">
        <div class="flex items-center border-b px-3">
          <Search class="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            v-model="searchQuery"
            @keydown="handleInputKeyDown"
            placeholder="Search for kanjis..."
            class="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <ScrollArea class="max-h-[300px] overflow-y-auto">
          <div
            v-if="isLoading"
            class="py-6 flex items-center justify-center text-sm text-muted-foreground"
          >
            <Loader2 class="mr-2 h-4 w-4 animate-spin" />
            Searching...
          </div>

          <div
            v-else-if="searchResults.length === 0"
            class="py-6 text-center text-sm text-muted-foreground"
          >
            No results found.
          </div>

          <div v-else class="p-2">
            <div class="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              {{ searchQuery ? 'Search Results' : 'Recent Searches' }}
            </div>

            <button
              v-for="(result, index) in searchResults"
              :key="result"
              :ref="
                (el) => {
                  if (el) itemRefs[index] = el as HTMLElement
                }
              "
              @click="goToKanji(result)"
              @mousemove="selectedIndex = index"
              :class="[
                'relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                selectedIndex === index ? 'bg-accent text-accent-foreground' : 'transparent',
              ]"
            >
              <History v-if="!searchQuery" class="mr-2 h-4 w-4 text-muted-foreground" />
              {{ result }}
            </button>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  </div>
</template>
